import React, { type ReactNode, useContext } from 'react';
import { useImmerReducer } from 'use-immer';
import {
  ChoiceType,
  type Scope,
  type ScopeData,
  ScopeDataType,
  ScopeStatus,
} from '../../../state/server';

type View = {
  type: 'listviewselect' | 'listview' | 'view';
  data: ScopeData[];
};

export enum ScopeError {
  Unknown = 'unknown',
  AccessDenied = 'accessDenied',
  Scope = 'scope',
}

// Consent client state
export type ConsentClientStateType = {
  scope?: Scope;
  consent: boolean;
  reject: boolean;
  localization?: Record<string, string>;
  isLoading: boolean;
  hero?: ScopeData;

  views: View[];
  currentViewIndex: number;
  values: Record<string, string[]>;
  updateScopeData: boolean;
  error?: ScopeError;
};

// Initial consent state
const initialClientState: ConsentClientStateType = {
  scope: undefined,
  consent: false,
  reject: false,
  localization: undefined,
  isLoading: true,
  hero: undefined,

  views: [],
  currentViewIndex: 0,
  values: {},
  updateScopeData: false,
  error: undefined,
};

// All action types that are dispatched in the consent client.
type ConsentClientActionType =
  | {
      type: 'setScopeAndLocalization';
      payload: {
        scope: Scope;
        localization: Record<string, string> | undefined;
      };
    }
  | { type: 'consent' }
  | { type: 'reject' }
  | { type: 'previous view' }
  | { type: 'next view' }
  | { type: 'setValues'; payload: { values: Record<string, string[]> } }
  | { type: 'setIsLoading'; payload: { isLoading: boolean } }
  | { type: 'update and next view' }
  | { type: 'setError'; payload: { error: ScopeError } };

/**
 * A reducer function for the application that updates it's consent client state given an action.
 * @param draft A proxy to the original base state that can be safely mutated
 * @param action The ConsentActionType action dispatched
 */
function ConsentClientReducer(
  draft: ConsentClientStateType,
  action: ConsentClientActionType,
) {
  switch (action.type) {
    case 'setScopeAndLocalization': {
      const scope = action.payload.scope;

      draft.scope = scope;
      draft.localization = action.payload.localization;
      if (draft.updateScopeData) {
        draft.updateScopeData = scope.status === ScopeStatus.Ready;
      }

      if (scope) {
        draft.views = [];
        draft.hero = scope.data.find((d) => d.hero === true);
        const selectables = scope.data.filter(
          (d) => d.type === ScopeDataType.StringArray,
        );
        const nonSelectables = scope.data.filter(
          (d) => d.type !== ScopeDataType.StringArray && !d.hero,
        );

        selectables
          .filter((d) => d.choiceType !== ChoiceType.None)
          .forEach((d) => {
            draft.views.push({ type: 'listviewselect', data: [d] });
          });

        selectables
          .filter((d) => d.choiceType === ChoiceType.None)
          .forEach((d) => {
            draft.views.push({ type: 'listview', data: [d] });
          });

        if (nonSelectables.length > 0) {
          draft.views.push({ type: 'view', data: nonSelectables });
        }

        if (draft.isLoading && !draft.consent && !draft.reject) {
          draft.isLoading = scope.status === ScopeStatus.PendingData;
        }

        if (
          scope.status === ScopeStatus.Error ||
          scope.status === ScopeStatus.Expired
        ) {
          draft.error = ScopeError.Scope;
        }
      }
      break;
    }
    case 'consent': {
      draft.consent = true;
      draft.isLoading = true;
      break;
    }
    case 'previous view':
    case 'reject': {
      draft.reject = true;
      draft.isLoading = true;
      break;
    }
    case 'setIsLoading': {
      draft.isLoading = action.payload.isLoading;
      break;
    }
    case 'setValues': {
      draft.values = action.payload.values;
      break;
    }
    // case "previous view": {
    //   if (draft.currentViewIndex === 0) {
    //     // first step - nothing previous - should reject
    //     draft.reject = true;
    //     draft.isLoading = true;
    //   } else {
    //     draft.currentViewIndex = draft.currentViewIndex - 1;
    //   }
    //   break;
    // }
    case 'next view': {
      if (draft.currentViewIndex === draft.views.length - 1) {
        // last step - nothing next - should approve
        draft.consent = true;
        draft.isLoading = true;
      } else {
        draft.currentViewIndex = draft.currentViewIndex + 1;
        draft.isLoading = true;
      }
      break;
    }
    case 'update and next view': {
      draft.updateScopeData = true;
      break;
    }
    case 'setError': {
      draft.error = action.payload.error;
      draft.isLoading = false;
      break;
    }
  }
}

// The Consent Context type used in the application.
type ConsentClientContextType = [
  ConsentClientStateType,
  React.Dispatch<ConsentClientActionType>,
];

/**
 * The React Context object that components subscribe to.
 * The default value for the context is the previously defined initial state.
 * See https://reactjs.org/docs/context.html#reactcreatecontext for more documentation.
 */
const ConsentClientContext = React.createContext<ConsentClientContextType>([
  initialClientState,
  () => {},
]);

/**
 * A hook that returns the consent context.
 * @returns The consent context, current state and dispatch function
 */
export function useConsentClientContext(): ConsentClientContextType {
  return useContext(ConsentClientContext);
}

/**
 * Component that allows consuming components to subscribe to context changes.
 * @param props Optional children components
 * @returns Consent context provider
 */
export function ConsentClientContextProvider(props: {
  children?: ReactNode;
  initialState?: ConsentClientStateType;
}) {
  const init: ConsentClientStateType = {
    ...initialClientState,
    ...props.initialState,
  };
  const [state, dispatch] = useImmerReducer(ConsentClientReducer, init);

  return (
    <ConsentClientContext.Provider value={[state, dispatch]}>
      {props.children}
    </ConsentClientContext.Provider>
  );
}
